import React, { useState, useEffect, useRef } from "react";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import "./FilledFormsList.css";
import usePageTitle from "../usePageTitle";
import FilledFormsListHeaderComponent from "./FilledFormsListHeaderComponent";
import FilledFormsListDatatableComponent from "./FilledFormsListDatatableComponent";
import { FilterMatchMode } from "primereact/api";
import { ProgressSpinner } from "primereact/progressspinner";

import { fetchFilledForms } from "./api";

const FilledFormsList = () => {
  usePageTitle("Filled Forms List - SkeyeForm™");
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(24);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  // eslint-disable-next-line
  const toast = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const filledForms = await fetchFilledForms();
        if (Array.isArray(filledForms)) {
          const mappedForms = filledForms.map((form) => ({
            id: form.id,
            formSchemaId: form.formSchemaId,
            formTitle: form.formTitle,
            createdAt: form.createdAt,
            data: form.data,
            label: form.label,
            submissionId: form.submissionId,
          }));
          setTotalRecords(mappedForms.length);
          setData(mappedForms);
          setFilteredData(mappedForms.slice(first, first + rows));
        } else {
          console.error("Unexpected response format:", filledForms);
        }
      } catch (error) {
        console.error("Error fetching filled Forms:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [first, rows]);

  useEffect(() => {
    if (Array.isArray(data)) {
      let filtered = data.filter((form) => {
        const formattedDate = form.createdAt
          ? new Date(form.createdAt).toLocaleString("en-US", {
              month: "short",
              day: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })
          : "";
        return (
          (form.label && form.label.toString().includes(globalFilterValue)) ||
          (form.formTitle &&
            form.formTitle.toString().includes(globalFilterValue)) ||
          (formattedDate && formattedDate.includes(globalFilterValue))
        );
      });

      if (sortField) {
        filtered = filtered.sort((a, b) => {
          const valueA = a[sortField];
          const valueB = b[sortField];
          let result = 0;

          if (valueA < valueB) result = -1;
          else if (valueA > valueB) result = 1;

          return sortOrder * result;
        });
      }

      setFilteredData(filtered.slice(first, first + rows));
      setTotalRecords(filtered.length);
    }
  }, [globalFilterValue, first, rows, data, sortField, sortOrder]);

  const onPageChange = (e) => {
    setFirst(e.first);
    setRows(e.rows);
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setGlobalFilterValue(value);
    setFirst(0);
  };

  const clearFilter = () => {
    setGlobalFilterValue("");
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
  };

  const onSort = (e) => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder);
  };

  const header = (
    <FilledFormsListHeaderComponent
      globalFilterValue={globalFilterValue}
      onGlobalFilterChange={onGlobalFilterChange}
      clearFilter={clearFilter}
    />
  );

  return (
    <div className="p-grid p-dir-col table-page">
      <div className="p-col title">
        <h1>Filled Forms List</h1>
        <div className="title-bar"></div>
      </div>
      {loading ? (
        <div className="loader-container">
          <ProgressSpinner
            height={40}
            width={40}
            color="#D97A07"
            wrapperStyle={{ display: "inline-block", verticalAlign: "middle" }}
            wrapperClass=""
            visible={true}
            ariaLabel="oval-loading"
            secondaryColor="#D97A07"
            strokeWidth={2}
            strokeWidthSecondary={2}
          />
          <span
            style={{
              marginLeft: "10px",
              verticalAlign: "middle",
              fontSize: "20px",
              fontFamily: "Poppins",
            }}
          >
            Fetching Data (This Might Take a Minute)...
          </span>
        </div>
      ) : (
        <FilledFormsListDatatableComponent
          data={filteredData}
          filters={filters}
          setFilters={setFilters}
          header={header}
          first={first}
          rows={rows}
          totalRecords={totalRecords}
          onPageChange={onPageChange}
          onSort={onSort}
          sortField={sortField}
          sortOrder={sortOrder}
          emptyMessage="No filled forms found"
        />
      )}
    </div>
  );
};

export default FilledFormsList;
